import {ApiError, ApiResponse, useApiMutation, useApiQuery} from "../../../api/useApi";
import {MutationOptions, useQueryClient, UseQueryOptions} from "react-query";
import {Language} from "../../../api/useLanguage";
import {FacilitySocialLink} from "./useFacilitySocialLinks";


// #################################################
//                      TYPES
// #################################################
/**
 * Facility type
 * @typedef {Object} Facility
 */
export type Facility = {
    id: number;
    facilities?: Facility[]|null;
    group_id?: number | null;
    is_group?: boolean;
    /** Hôtel qui porte la bibliothèque officielle de modèles (voir `isTemplateLibraryMode`). */
    is_template_source?: boolean;
    /** Origine de l'affectation quand l'hôtel est lu via un flow ou un formulaire. */
    is_manual?: boolean;
    is_from_rule?: boolean;
    // Mode démo (lecture seule, posé par migration uniquement)
    is_demo?: boolean;
    language_id?: number | null;
    language?: Language;
    default_language_id?: number | null;
    default_language?: Language;
    languages?: Language[];
    currency_id?: number | null;
    currency?: {
        id: number;
        shortname: string;
        symbol: string;
        name: string;
    } | null;
    name?: string;
    address?: string;
    city?: string;
    zip_code?: string;
    country_id?: number | null;
    timezone?: string;
    email?: string;
    phone?: string;
    website?: string;
    booking_page_url?: string;
    checkin_hour?: string;
    checkout_hour?: string;
    is_all_country_sms?: boolean;
    auto_sync_room_upgrade?: boolean;
    sms_sender_name?: string;
    sms_provider_id?: number | null;
    use_credit_alert?: boolean;
    credit_alert_emails?: string[] | null;
    amount_email_credit_alert?: number;
    amount_ai_credit_alert?: number;
    amount_bot_credit_alert?: number;
    amount_sms_credit_alert?: number | null;
    sms_credit?: number | null;
    subscription_id?: number | null;
    subscription_started_at?: string | null;
    subscription_ended_at?: string | null;
    subscription_amount?: number | null;
    renewal_type?: string | null;
    pipeline_stage_id?: number | null;
    pipeline_stage_name?: string | null;
    pipeline_stage_color?: string | null;
    special_status?: string | null;
    plug_id?: number | null;
    plug_name?: string | null;
    // Le PMS connecté remonte-t-il le nombre d'occupants par chambre ? (gating du mode « par occupant »)
    provides_room_occupancy?: boolean;
    // Ce que le PMS fait d'une commande validée (case « Envoyer au PMS » des produits et catalogues)
    pms_capabilities?: PmsCapabilities;
    business_provider_id?: number | null;
    latitude?: number | null;
    longitude?: number | null;
    admin_email_limit?: number | null;
    admin_segment_limit?: number | null;
    admin_flow_limit?: number | null;
    admin_active_form_limit?: number | null;
    admin_ai_credits_limit?: number | null;
    admin_has_utelys?: boolean;
    admin_has_ai_bot?: boolean;
    bot_plan_id?: number | null;
    bot_plan_name?: string | null;
    bot_subscription_status?: 'active' | 'paused' | 'cancelled' | null;
    admin_has_payment?: boolean;
    admin_has_loyalty?: boolean;
    admin_has_reviews?: boolean;
    admin_has_housekeeping?: boolean;
    /** Messagerie interne du ménage, allumée hôtel par hôtel. */
    housekeeping_messaging_enabled?: boolean;
    admin_has_companies?: boolean;
    admin_has_booking_requests?: boolean;
    admin_has_events?: boolean;
    admin_has_customer_interaction?: boolean;
    admin_has_mailbox_selection?: boolean;
    admin_has_meta_messaging?: boolean;
    active_loyalty_program_id?: number | null;
    use_loyalty_levels?: boolean | null;
    has_active_review_connections?: boolean;
    has_stripe_connected?: boolean;
    whatsapp_meta_business_id?: string;
    whatsapp_phone_number_id?: string;
    incoming_message_alert_emails?: string;
    review_alert_emails?: string;
    use_review_alert?: boolean;
    use_order_alerts?: boolean;
    review_alert_threshold?: number;
    review_alert_recap_frequency?: string;
    use_send_errors_digest?: boolean;
    send_errors_alert_emails?: string;
    ota_email_domains?: string;
    room_count?: number | null;
    staff_count?: number | null;
    star_rating?: string | null;
    social_links?: FacilitySocialLink[];
    show_tf_branding?: boolean;
    show_tf_email_branding?: boolean;
    /** 2FA obligatoire pour les utilisateurs de l'hôtel (ou des hôtels du groupe). */
    require_two_factor?: boolean;
    primary_admin_user_id?: number | null;
    primary_admin_email?: string | null;
    created_at?: string;
    updated_at?: string;
    deleted_at?: string;
    // Formulaires pris en compte par la colonne Check-in du dashboard (null = défaut : formulaire d'arrivée)
    checkin_survey_ids?: number[] | null;
    effective_checkin_survey_ids?: number[];
    effective_checkin_surveys?: { id: number; name: string }[];
    [key: string]: any;
}

/**
 * Type étendu avec les établissements enfants (pour les groupes)
 */
export type FacilityWithChildren = Facility & {
    child_facilities?: Facility[];
}

/**
 * Type pour les facilities avec coordonnées GPS (pour la carte)
 */
export type FacilityWithCoordinates = {
    id: number;
    name: string;
    email: string;
    address: string;
    city: string;
    zip_code: string;
    latitude: number;
    longitude: number;
    is_group: boolean;
    pipeline_stage_id: number | null;
    special_status: string | null;
    pipeline_stage?: {
        id: number;
        name: string;
        slug: string;
        color: string;
    };
    groups?: {
        id: number;
        name: string;
    }[];
}

// #################################################
//                      QUERIES
// #################################################

/**
 * Hook to get all facilities of current user
 * @returns Facility[]
 */
export function useFacilities(opts?: { fromContext?: string }) {
    const params = new URLSearchParams();
    if (opts?.fromContext) params.set("from", opts.fromContext);
    const qs = params.toString();
    const url = `/facilities${qs ? `?${qs}` : ""}`;
    return useApiQuery<Facility[]>(url);
}

/**
 * Filtres optionnels de la liste super admin (`/facilities-all`).
 * `none` sur l'étape ou le PMS : établissements qui n'en ont pas.
 */
export type AllFacilitiesFilters = {
    pipeline_stage_id?: string;
    special_status?: string;
    plug_id?: string;
    sort?: 'name' | 'created_at';
    direction?: 'asc' | 'desc';
};

/**
 * Hook to get all facilities with pagination
 * @param {number} page - Current page number
 * @param isGroup
 * @param searchTerm
 * @param filters - étape, statut spécial, PMS, tri
 * @returns {<ApiResponse<Facility[]>, ApiError>}
 */
export function useAllFacilities(
    page: number = 1,
    isGroup: boolean = false,
    searchTerm: string = "",
    filters: AllFacilitiesFilters = {}
) {
    const pageSize = 10;
    const params = new URLSearchParams({
        page: String(page),
        pageSize: String(pageSize),
        is_group: isGroup ? "1" : "0",
    });
    if (searchTerm.trim()) {
        params.append("search", searchTerm.trim());
    }
    (Object.keys(filters) as (keyof AllFacilitiesFilters)[]).forEach((key) => {
        const value = filters[key];
        if (value) {
            params.append(key, String(value));
        }
    });
    const url = `/facilities-all?${params.toString()}`;
    // Le tableau précédent reste affiché pendant le chargement de la page suivante.
    return useApiQuery<ApiResponse<Facility[]>>(url, {}, { keepPreviousData: true });
}

/**
 * Hook to get all facilities with pagination
 * @param isGroup
 * @returns {<ApiResponse<Facility[]>, ApiError>}
 */
export function useAllGroupFacilities(isGroup: boolean = false) {
    const url = `/facilities-group-all?is_group=${isGroup ? 1 : 0}\`;`;
    return useApiQuery<ApiResponse<Facility[]>>(url);
}

/**
 * Hook to get all facilities with GPS coordinates (for map view)
 * Super admin only
 * @returns FacilityWithCoordinates[]
 */
export function useFacilitiesWithCoordinates() {
    const url = '/facilities-with-coordinates';
    return useApiQuery<FacilityWithCoordinates[]>(url);
}

/**
 * Hook to get the groups of the facility
 * @returns {<ApiResponse<Facility[]>, ApiError>}
 */
export function useFacilityGroups() {
    const url = `/facility-groups-accepted`;
    return useApiQuery<ApiResponse<Facility[]>>(url);
}

/**
 * Hook to get a single facility
 * @param {number | string | null} id
 * @returns Facility
 */
export function useFacility(id: number | string | null) {
    const url = id ? `/facilities/${id}` : '';
    return useApiQuery<Facility>(url);
}

/**
 * Hook to get a facility with its child facilities (for groups)
 * @param {number | null} facilityId - The facility ID (should be a group)
 * @returns FacilityWithChildren
 */
export function useFacilityWithChildren(facilityId: number | null | undefined) {
    const url = facilityId ? `/facilities/${facilityId}/with-children` : '';
    return useApiQuery<FacilityWithChildren>(url, {}, {
        enabled: !!facilityId,
    });
}


/**
 * Hook to get a single facility with its favorite surveys
 * @returns Facility
 */
export function useFacilityFavoriteSurveys() {
    const url = `/facility-favorite-surveys`
    return useApiQuery<Facility>(url);
}

/**
 * Hook to delete a facilities
 * @returns Facility
 */
export function Facility() {
    const queryClient = useQueryClient();

    // Notez maintenant que nous passons une fonction pour générer l'URL basée sur l'ID
    return useApiMutation<Facility, ApiError, { id: number }>(
        'delete',
        ({ id }) => `/facilities/${id}`,
        {
            // Ajoutez la logique onSuccess ici si nécessaire
            onSuccess: () => {
                // Invalidez les requêtes de liste d'utilisateurs ici si nécessaire
                // Invalidate all queries that start with '/facilities'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/facilities'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: false // Optionally, set to true if you want to refetch inactive queries as well
                });
            },
        }
    );
}

/**
 * Hook to update a facility
 * @param {MutationOptions<Facility, ApiError, Facility>} options
 */
export function useUpdateFacility(options: MutationOptions<Facility, ApiError, Facility> = {}) {

    const queryClient = useQueryClient();

    return useApiMutation<Facility, ApiError, Facility>(
        'put',
        (variables) => `/facilities/${variables.id}`,
        {
            ...options,
            onSuccess: (data, variables, context) => {

                // Invalide les requêtes nécessaires:
                queryClient.invalidateQueries(`/facilities/${variables.id}`);

                // Invalidate all queries that start with '/facilities'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/facilities'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: false // Optionally, set to true if you want to refetch inactive queries as well
                });

                // Invalidate all queries that start with '/facilities'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/users'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: false // Optionally, set to true if you want to refetch inactive queries as well
                });

                // Les soldes et seuils de /consumables sont dérivés de colonnes
                // de `facilities` : sans cette invalidation, l'écran de réglages
                // réaffichait l'ancien seuil juste après un enregistrement réussi.
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && String(query.queryKey[0]).startsWith('/consumables'),
                    refetchActive: true,
                    refetchInactive: false
                });

                // Assurez-vous de propager l'appel à l'onSuccess original, si fourni
                options.onSuccess?.(data, variables, context);
            },
            onError: (error, variables, context) => {
                console.error("An error occurred while updating the facilities", error);
                // Propagation de l'appel à l'onError original, si fourni
                options.onError?.(error, variables, context);
            },
        }
    );
}


/**
 * Hook to update a facility
 * @param {MutationOptions<Facility, ApiError, Facility>} options
 */
export function useUpdateIsAllCountrySmsFacility(options: MutationOptions<Facility, ApiError, Facility> = {}) {

    const queryClient = useQueryClient();

    return useApiMutation<Facility, ApiError, Facility>(
        'put',
        (variables) => `/facilities/is_all_country_sms/${variables.id}`,
        {
            ...options,
            onSuccess: (data, variables, context) => {

                // Invalide les requêtes nécessaires:
                queryClient.invalidateQueries(`/facilities/${variables.id}`);

                // Invalidate all queries that start with '/facilities'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/facilities'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: false // Optionally, set to true if you want to refetch inactive queries as well
                });

                // Invalidate all queries that start with '/facilities'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/users'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: false // Optionally, set to true if you want to refetch inactive queries as well
                });

                // Les soldes et seuils de /consumables sont dérivés de colonnes
                // de `facilities` : sans cette invalidation, l'écran de réglages
                // réaffichait l'ancien seuil juste après un enregistrement réussi.
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && String(query.queryKey[0]).startsWith('/consumables'),
                    refetchActive: true,
                    refetchInactive: false
                });

                // Assurez-vous de propager l'appel à l'onSuccess original, si fourni
                options.onSuccess?.(data, variables, context);
            },
            onError: (error, variables, context) => {
                console.error("An error occurred while updating the facilities", error);
                // Propagation de l'appel à l'onError original, si fourni
                options.onError?.(error, variables, context);
            },
        }
    );
}


/**
 * Hook to toggle the "show_tf_branding" flag on a facility (super-admin only).
 * Calls the dedicated /super-admin/facilities/{id}/tf-branding endpoint
 * and invalidates the relevant facility caches on success.
 */
export function useUpdateTfBranding() {
    const queryClient = useQueryClient();

    return useApiMutation<{ data: { show_tf_branding: boolean } }, ApiError, { id: number; show_tf_branding: boolean }>(
        'patch',
        ({ id }) => `/super-admin/facilities/${id}/tf-branding`,
        {
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries(`/facilities/${variables.id}`);
                queryClient.invalidateQueries({
                    predicate: (query) =>
                        Array.isArray(query.queryKey) &&
                        typeof query.queryKey[0] === 'string' &&
                        query.queryKey[0].startsWith('/facilities'),
                    refetchActive: true,
                    refetchInactive: false,
                });
            },
        }
    );
}

/**
 * Hook to toggle the "show_tf_email_branding" flag on a facility (super-admin only):
 * the "Sent with TriggerFlow" footer appended to outgoing emails.
 * Calls the dedicated /super-admin/facilities/{id}/tf-email-branding endpoint
 * and invalidates the relevant facility caches on success.
 */
export function useUpdateTfEmailBranding() {
    const queryClient = useQueryClient();

    return useApiMutation<{ data: { show_tf_email_branding: boolean } }, ApiError, { id: number; show_tf_email_branding: boolean }>(
        'patch',
        ({ id }) => `/super-admin/facilities/${id}/tf-email-branding`,
        {
            onSuccess: (_data, variables) => {
                queryClient.invalidateQueries(`/facilities/${variables.id}`);
                queryClient.invalidateQueries({
                    predicate: (query) =>
                        Array.isArray(query.queryKey) &&
                        typeof query.queryKey[0] === 'string' &&
                        query.queryKey[0].startsWith('/facilities'),
                    refetchActive: true,
                    refetchInactive: false,
                });
            },
        }
    );
}


/**
 * Hook to update a facility
 * @param {MutationOptions<Facility, ApiError, Facility>} options
 */
export function useUpdateFavoriteSurveyFacility(options: MutationOptions<Facility, ApiError, Facility> = {}) {

    const queryClient = useQueryClient();

    return useApiMutation<Facility, ApiError, Facility>(
        'put',
        (variables) => `/facility-favorite-surveys/${variables.id}`,
        {
            ...options,
            onSuccess: (data, variables, context) => {

                // La lecture des favoris (`/facility-favorite-surveys`) et le tableau des
                // réservations (colonne Check-in) ne commencent pas par `/facilities` :
                // sans cette invalidation ils gardaient l'ancien réglage.
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey)
                        && typeof query.queryKey[0] === 'string'
                        && (query.queryKey[0].startsWith('/facility-favorite-surveys')
                            || query.queryKey[0].startsWith('/dashboard-reservations')),
                });

                // Invalide les requêtes nécessaires:
                queryClient.invalidateQueries(`/facilities/${variables.id}`);

                // Invalidate all queries that start with '/facilities'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/facilities'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: false // Optionally, set to true if you want to refetch inactive queries as well
                });

                // Invalidate all queries that start with '/facilities'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/users'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: false // Optionally, set to true if you want to refetch inactive queries as well
                });

                // Les soldes et seuils de /consumables sont dérivés de colonnes
                // de `facilities` : sans cette invalidation, l'écran de réglages
                // réaffichait l'ancien seuil juste après un enregistrement réussi.
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && String(query.queryKey[0]).startsWith('/consumables'),
                    refetchActive: true,
                    refetchInactive: false
                });

                // Assurez-vous de propager l'appel à l'onSuccess original, si fourni
                options.onSuccess?.(data, variables, context);
            },
            onError: (error, variables, context) => {
                console.error("An error occurred while updating the facilities", error);
                // Propagation de l'appel à l'onError original, si fourni
                options.onError?.(error, variables, context);
            },
        }
    );
}

/**
 * Réponse de POST /facilities : `onboarding_invitation` n'est présent que si
 * l'envoi de l'e-mail d'accès a été demandé à la création.
 */
export type CreatedFacility = Facility & {
    onboarding_invitation?: { sent: boolean; recipient_email?: string };
};

/**
 * Hook to create a facilities
 * @param {MutationOptions<CreatedFacility, ApiError, Facility>} options
 */
export function useCreateFacility(options: MutationOptions<CreatedFacility, ApiError, Facility> = {}) {
    const queryClient = useQueryClient();

    return useApiMutation<CreatedFacility, ApiError, Facility>(
        'post',
        () => '/facilities',
        {
            ...options,
            onSuccess: (data, variables, context) => {
                // Invalidate all queries that start with '/facilities'
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && query.queryKey[0].startsWith('/facilities'),
                    refetchActive: true, // Refetch active queries automatically
                    refetchInactive: false // Optionally, set to true if you want to refetch inactive queries as well
                });
                queryClient.invalidateQueries(`/user`);
                options.onSuccess?.(data, variables, context);
            },
            onError: (error, variables, context) => {
                console.error("An error occurred", error);
                options.onError?.(error, variables, context);
            },
        }
    );
}

/**
 * Supprime un établissement (super admin). `confirm_name` doit être le nom exact.
 */
export function useDeleteFacility(options: MutationOptions<void, ApiError, { id: number; confirm_name: string }> = {}) {
    const queryClient = useQueryClient();

    return useApiMutation<void, ApiError, { id: number; confirm_name: string }>(
        'delete',
        ({ id }) => `/facilities/${id}`,
        {
            ...options,
            onSuccess: (data, variables, context) => {
                queryClient.invalidateQueries({
                    predicate: query => Array.isArray(query.queryKey) && String(query.queryKey[0]).startsWith('/facilities'),
                });
                options.onSuccess?.(data, variables, context);
            },
        }
    );
}

/**
 * Hook to send a test email
 * @returns {<ApiResponse<Facility>, ApiError>}
 */
export function useSendTestEmail() {
    return useApiMutation(
        'post',
        (payload) => `/facilities/test-smtp/${payload.facility_id}`, // Utilisez l'id du facility
        {
            onSuccess: () => {
                // Email de test envoyé avec succès
            },
            onError: () => {
                // Erreur gérée par le composant appelant
            }
        }
    );
}


/** Capacités du PMS sur les commandes (FacilityResource::pms_capabilities). */
export interface PmsCapabilities {
    /** Un PMS connecté reçoit de vraies lignes de vente. */
    push_order_lines: boolean;
    /** Les ventes sont notées dans le mémo de la réservation (Medialog, interrupteur allumé). */
    push_order_memo: boolean;
    /** Un PMS connecté sait noter les ventes en mémo : l'interrupteur peut être allumé. */
    order_memo_available?: boolean;
    /** PMS à nommer dans les libellés. */
    pms_name: string | null;
}

/** Allume ou coupe la note des ventes dans le mémo de la réservation (établissement sélectionné). */
export function useUpdatePmsOrderMemo(options: MutationOptions<{ pms_capabilities: PmsCapabilities }, ApiError, { enabled: boolean }> = {}) {
    return useApiMutation<{ pms_capabilities: PmsCapabilities }, ApiError, { enabled: boolean }>(
        'put',
        () => '/facility/pms-order-memo',
        options
    );
}

export interface FacilityThaisInfo {
    subdomain: string;
}

export function useFacilityThaisInfo(
    // On accepte les options de React-Query en 3ᵉ paramètre
    options?: UseQueryOptions<FacilityThaisInfo, ApiError>
) {
    const url = "/facility-thais-info";
    return useApiQuery<FacilityThaisInfo>(
        url,
        {},   // <-- 1) config Axios vide ou surchargé si besoin
        {
            // 2) ici vos options React-Query
            enabled: options?.enabled ?? true,
            ...options,
        }
    );
}




